import React, { memo, useEffect } from 'react'
import { GetServerSideProps, GetServerSidePropsContext, NextPage } from 'next'
import {
  Box,
  GoogleMaps,
  Marker,
  useStoreQuery,
  Spinner,
  Heading,
  LinkButton,
} from '@thirstycamel/ui'
import { StoreEntity } from '@ts/core/src/modules/store/store.entity'
import { useRouter } from 'next/router'
import { QueryCache } from 'react-query'
import { dehydrate } from 'react-query/hydration'
import ReactMarkdown from 'react-markdown'
import '../../components/SearchResults/markdown-styles.css'

import LayoutMain from '../../layout/LayoutMain'
import StoreCard from '../../components/StoreCard'
import { buildStoreSchema } from '../../utils/schema'
import { backend } from '../../utils/backend'

const MAP_OFFSET = 0.015

interface StoresSlugProps {
  notFound?: boolean
}

const StoresSlug: NextPage<StoresSlugProps> = ({ notFound }) => {
  const router = useRouter()

  const slug = router.query.slug as string

  const store = useStoreQuery<StoreEntity>({ store: notFound ? undefined : slug })

  useEffect(() => {
    if (store.data && !store.data.published) {
      router.replace('/store-locator')
    }
  }, [store.data, router])

  if (notFound) {
    return (
      <LayoutMain title="Store not found">
        <Box textAlign="center" py={10}>
          <Heading mb={6}>Store not found.</Heading>

          <LinkButton href="/" variantColor="pink">
            Homepage
          </LinkButton>
        </Box>
      </LayoutMain>
    )
  }

  if (store.data && !store.data.published) {
    return null
  }

  if (!store.data) {
    if (store.isFetching) {
      return (
        <LayoutMain title="Loading...">
          <Spinner color="pink.500" my={10} mx="auto" display="block" />
        </LayoutMain>
      )
    }

    return (
      <LayoutMain title="Store not found">
        <Box textAlign="center" py={10}>
          <Heading mb={6}>{store.error ? String(store.error) : 'Store not found.'}</Heading>

          <LinkButton href="/" variantColor="pink">
            Homepage
          </LinkButton>
        </Box>
      </LayoutMain>
    )
  }

  const [lng, lat] = store.data.point?.coordinates || []
  const canonicalUrl = `https://www.thirstycamel.com.au/stores/${store.data.slug}`
  const structuredData = buildStoreSchema(store.data)
  const title = store.data.meta_title || store.data.name
  const metaDescription = store.data.meta_description || undefined

  return (
    <LayoutMain
      title={title}
      description={metaDescription}
      meta_title={store.data.meta_title || undefined}
      meta_description={metaDescription}
      ogTitle={title}
      ogDescription={metaDescription}
      canonicalUrl={canonicalUrl}
      structuredData={structuredData}
      noContainer
    >
      <Box
        position="relative"
        h={[, , '70vh']}
        minHeight={[, , '500px']}
        px={[6, , 0]}
        py={[10, , 0]}
        bg={['gray.50', , 'white']}
      >
        <Box display={['none', , 'flex']} h="100%" w="100%">
          <GoogleMaps zoom={14} center={{ lat, lng: lng - MAP_OFFSET }}>
            <Marker position={{ lat, lng }} />
          </GoogleMaps>
        </Box>

        <Box
          position={[, , 'absolute']}
          top={[, , '50%']}
          right={[, , '70%']}
          transform={[, , 'translate(50%,-50%)']}
          w={[, , '50%']}
          maxW={[, , '450px']}
        >
          <StoreCard store={store.data} boxShadow={[, , 'lg']} />
        </Box>
      </Box>

      {store.data.seo_description && (
        <Box
          className="markdown-content"
          maxW="1000px"
          mr="auto"
          ml={[3, 5, 'calc(30vw - min(25vw, 225px))']}
          pr={[3, 5]}
          py={10}
          textAlign="left"
        >
          <ReactMarkdown>{store.data.seo_description}</ReactMarkdown>
        </Box>
      )}
    </LayoutMain>
  )
}

export const getServerSideProps: GetServerSideProps = async (
  context: GetServerSidePropsContext,
) => {
  const slugParam = context.params?.slug

  if (typeof slugParam !== 'string') {
    context.res.statusCode = 404
    return { props: { notFound: true } }
  }

  try {
    const store = await backend<StoreEntity>(`/stores/${slugParam}`)

    if (!store?.published) {
      context.res.writeHead(302, { Location: '/store-locator' })
      context.res.end()

      return { props: { notFound: true } }
    }

    const queryCache = new QueryCache()
    await queryCache.prefetchQuery([`stores/${slugParam}`], backend, { cacheTime: 30000 })

    return {
      props: {
        dehydratedState: dehydrate(queryCache),
        notFound: false,
      },
    }
  } catch (e) {
    context.res.statusCode = 404
    return { props: { notFound: true } }
  }
}

export default memo(StoresSlug)
